import lodash from 'lodash'
import authentication from '@/authentication'
import * as usersApi from '@/api/users/request'
import * as userUtil from '@/api/users/util'
import router from '@/router'
import types from '@/store/mutation-types'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import { getAccountById } from '@/api/accounts/request'
import { getCurUserInfoByAdId } from '@/api/users/users.request'
import { resetStoreWhenLogout } from '@/store/util'
import { AdminRoleName, ContractorAdminRoleName, dashboardRouterNames, TenantRoleName, UserDomain, UserSource, UserType, VendorRolesName } from '@/utils/consts'
import eventBus, { EVENT_FLAG } from '@/utils/eventBus'
import { tenancyType } from '@/utils/locale'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'

/**
 * @typedef {Object} State
 * @prop {Object} currentUser
 * @prop {string} currentUser.id
 * @prop {string} currentUser.AD_id
 * @prop {string} currentUser.source
 * @prop {string} currentUser.dashboard
 * @prop {string} currentUser.domain
 * @prop {string} currentUser.username
 * @prop {string} currentUser.name
 * @prop {{ name: string, usertype: string }[]} currentUser.roles
 * @prop {{ code: string, zones: string[] }[]} currentUser.buildings
 */
/**
 * @type {State}
 */
const defaultState = {
  currentUser: null,
  currentGlobalUser: null,
  currentUserContact: null,
  visitedTenancyRecord: [],
}

// getters
const getters = {
  /**
   * @param {typeof defaultState} state
   * @returns {typeof defaultState['currentUser']}
   */
  currentUser: state => {
    let user = state.currentUser
    if (user) {
      user.name = userHelper.getUserName(user.username, user.displayName)
      return user
    } else {
      return {}
    }
  },
  currentUserBuildings: state => {
    const buildings = lodash.get(state, 'currentUser.buildings', [])
    return lodash.uniqBy(buildings, 'id')
  },
  /**
   * @returns {string}
   */
  currentUserEmail: state => {
    return userUtil.getPrimaryEmail(state.currentUser)
  },
  currentUserContact: state => state.currentUserContact,
  // External user for CMS
  isContractor (state) {
    return userUtil.isEXTAndCMSUser(state.currentUser)
  },
  /**
   * Vendor Admin Role for CMS
   * @param {typeof defaultState} state
   */
  isContractorAdmin (state) {
    return userUtil.isEXTAndCMSUser(state.currentUser) &&
      state.currentUser.roles.some(rec => ContractorAdminRoleName.includes(rec.name))
  },
  /**
   * @param {typeof defaultState} state
   */
  isVendorAccount (state) {
    return userUtil.isEXTAndCMSUser(state.currentUser) && state.currentUser.roles.some(row => VendorRolesName.includes(row.name))
  },
  /**
   * @param {typeof defaultState} state
   */
  isTenant (state) {
    return state.currentUser && state.currentUser.roles &&
      state.currentUser.roles.some(rec => TenantRoleName.includes(rec.name))
  },
  /**
   * @param {typeof defaultState} state
   */
  isEngineer (state) {
    return state.currentUser && state.currentUser.roles &&
      state.currentUser.roles.some(rec => Object.values(UserType.Staff).includes(rec.usertype))
  },
  /**
   * @param {typeof defaultState} state
   */
  isHKlandUser (state) {
    return state.currentUser && state.currentUser.source === UserSource.AD && state.currentUser.domain === UserDomain.CMS
  },
  /**
   * @param {typeof defaultState} state
   */
  isTrRep (state) {
    return aclHelper.getAccess(ccdPermsTypes.tenancy.review)
  },
  /**
   * @param {typeof defaultState} state
   */
  isAdmin (state) {
    return state.currentUser && state.currentUser.roles &&
      state.currentUser.roles.some(rec => AdminRoleName.includes(rec.name))
  },
  /**
   * @param {typeof defaultState} state
   */
  isStandaloneContractor (state) {
    return lodash.get(state, 'currentUser.roles', []).some((rec) => rec.name === 'TMS Standalone Contractor')
  },
  /**
   * @param {typeof defaultState} state
   */
  tenancyTypes (state) {
    return Object.entries(deep(state, 'currentUser.access.tenancies') || {})
      .reduce((result, [type, value]) => {
        if (value) {
          result.push(tenancyType[`${type}`])
        }
        return result
      }, [])
  },
  /**
   * @param {typeof defaultState} state
   */
  acTenancyIsOffice (state) {
    return lodash.get(state, 'currentUser.access.tenancies.Office', false)
  },
  /**
   * @param {typeof defaultState} state
   */
  acTenancyIsRetail (state) {
    return lodash.get(state, 'currentUser.access.tenancies.Retail', false)
  },
  /**
   * @param {typeof defaultState} state
   */
  zones (state) {
    let zones = []
    const buildings = lodash.get(state, 'currentUser.buildings', [])
    buildings.forEach(building => {
      building.zones.forEach(zone => {
        if (!zones.includes(zone)) {
          zones.push(zone)
        }
      })
    })
    return zones.sort()
  },
  buildings (state) {
    const buildings = lodash.get(state, 'currentUser.buildings', [])
    return Array.from(new Set(buildings.map(building => building.code))).sort()
  },
  currentGlobalUser (state) {
    return state.currentGlobalUser
  },
  userRegions (state) {
    return (state.currentGlobalUser && state.currentGlobalUser.regions) || []
  },
  userAdId (state) {
    const adId = lodash.get(state, ['currentUser', 'AD_id'], '')
    return adId
  },
  visitedTenancyRecord: state => state.visitedTenancyRecord,
}

// actions
const actions = {
  async reloadUser ({ commit, state, dispatch }, { adId }) {
    const currentUser = await getCurUserInfoByAdId(adId)
    const { user } = currentUser
    if (user && user.enabled && user.id && user.source) {
      commit(types.USER_UPDATE_CURRENT, { user })
      dispatch('loadContact', { externalId: user.externalId })
    }
    return currentUser
  },

  async loadContact ({ commit, getters }, { externalId, accountId, userId }) {
    let contact = null
    if (getters.isContractor) {
      contact = await usersApi.getUserContact(externalId)
    } else if (getters.isTenant) {
      const account = await getAccountById(accountId, userId) || {}
      const ccdContacts = account.users && deep(account.users.find((user) => user.id === userId), 'ccdContacts')
      contact = ccdContacts && ccdContacts[0]
    }
    commit(types.USER_CONTACT_CURRENT, { contact })
  },

  async postLoginAction ({ state, dispatch, commit }, commitObject) {
    commit(types.USER_UPDATE_CURRENT, commitObject)
    await dispatch('settings/loadAllSettingValues', null, { root: true })
    const regionalEnv = ['moduleSwitch.holidayWorkingHours', 'urls.workPermitQRLink', 'moduleSwitch.submissionInvoiceModule', 'moduleSwitch.hoistingModule', 'workPermit.sawpDescWorkEventMandatory']

    await dispatch('regions/loadRegionalEnv', regionalEnv, {root: true})

    notification.clearAll()
  },

  logout ({ getters, rootGetters }, options = {}) {
    const { finishCallback = () => {} } = options
    try {
      const name = getters.isHKlandUser ? dashboardRouterNames.login : dashboardRouterNames.publicLogin

      if (rootGetters['form/isPageDirty']) {
        router.push({ name, params: { action: 'logout' } }, finishCallback, finishCallback)
      } else {
        HTTP.post('logout')
        // @TODO <zoe_zhou@epam.com>
        // After switch region, the page is reloading, then the authentication instance is clear.
        if (authentication.authenticationContext) {
          authentication.signOut()
        }
        resetStoreWhenLogout()
        eventBus.emit(EVENT_FLAG.SYS_SIGNOUT)
        router.push({ name, params: {} }, finishCallback, finishCallback)
      }
    } catch (err) {
      console.error('Logout error', err)
    }
  },
  updateCurrentGlobalUser ({commit}, userInfo) {
    commit(types.CURRENT_GLOBAL_USER, lodash.cloneDeep(userInfo))
  },
  isLogin ({state}) {
    return !lodash.isEmpty(state.currentUser)
  },
}

// mutations
const mutations = {
  [types.USER_UPDATE_CURRENT] (state, { user }) {
    state.currentUser = user
  },
  [types.USER_CONTACT_CURRENT] (state, { contact }) {
    state.currentUserContact = contact
  },
  [types.CURRENT_GLOBAL_USER] (state, userInfo) {
    state.currentGlobalUser = userInfo
  },
  [types.USER_VISIT_TR] (state, tenancyRecordId) {
    if (!state.visitedTenancyRecord.includes(tenancyRecordId)) {
      state.visitedTenancyRecord.push(tenancyRecordId)
    }
  },
  [types.RESET_STATE] (state) {
    state.currentUser = null
    state.currentGlobalUser = null
    state.currentUserContact = null
    state.visitedTenancyRecord = []
  },
}

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
}
